// Baker 2: the structure draw (APP_BAKER §3 "One collider": kit + client draw path) — structures.bin drawn ONLY from artifacts: the SAME loaded bytes the
// StructCollider is built from (game/structures.js loadStructures), so the
// drawn triangle and the felt triangle are one triangle; ways.json supplies
// the rails over decks / bores and the bore frames the lamp shading measures
// along. No client-side shape derive, no per-object meshes — except an
// OPEN RAILING (owner 2026-10-16): its felt quad is collide-only, drawn as
// posts + rails over it (client/src/railing.js, the lit batch's thin tail).
//
// The plan (client/src/structplan.js, shared with the budget gate
// scripts/check-focus.mjs) decides every triangle by TABLE ROW: kind ->
// batch, material -> colour / tint, flags -> nosings. Per DRAW GROUP
// (DRAW.group_chunks^2 STR1 chunks, 1024 m) at most TWO merged, non-indexed
// meshes (§3.7):
//   lit  — MeshLambertMaterial: flat per-triangle normals, a flat per-material
//          vertex colour x a box-mapped detail texture (divided by its mean),
//          fog, DoubleSide (linings are TWO_SIDED and winding is not a facing
//          promise); STAIR triangles draw tread nosings at every riser of
//          HEIGHT in the same program (no discard); a TEXTURED material
//          THE FILL STONE (client/src/stonewall.js): a masonry / stone
//          triangle draws the procedural stone instead of the speckle (stair
//          code STONE_CODE, the stone's mean colour, its fitted (v, H) in uv)
//   bore — MeshBasicMaterial: roads3d boreTexture (injected by main.js:
//          new StructDraw(scene, cfg, {bore: boreTexture()}) — this module
//          imports no JSON and touches no DOM at load, so node gates such as
//          check-emit build it headless: no document -> no map) x lamp vertex shading x the
//          material tint, fog-free, DoubleSide — the bore look,
//          measured along the ways.json bore the record came from (srcKind
//          bores): uv = (lateral + height over floor, arc s) / bore_uv_m;
//          the vertex colour = tint x THE BORE LIGHT (structplan boreLight:
//          portal daylight + ceiling-lamp pools by tunnel type). (D1-TUNC)
//          + the bore rails (surface rail look), the ceiling luminaires
//          (owner-licensed invention 2026-10-11, drawn only) and a
//          near-sleeper block at the batch's end (update(): within
//          DRAW.tie_view_m), all untextured via a flat uv (FLAT_UV)
//   grate — (WAYS-G A3) the lit program ALPHA-TESTED: grating triangles
//          (MAT_DRAW grating) only; a procedural discard pattern by kind —
//          a funicular-track strip's sleepers along its fall line, a
//          funicular-stair sheet's open treads by world height (the
//          walkway riser, a nosing band at each tread edge); faded to solid
//          where the pattern spans under ~2 px; never blended
// A5 (opts.steps, L's look file pipeline/wayslook.json "steps"): the
// raised flights' STAIR triangles take its tread colour, riser_m and
// nosing band (nosing_m, its nosing / tread colour ratio) instead of
// DRAW.riser_m and the material colour — the raised flight and the ground
// stairs read as one stair. No steps -> today's nosings.
// NO_RENDER triangles are skipped. A kind / material with no table row throws
// (fail fast, never a silent skip).
//
// Residency (update): a chunk is drawn while its bounding sphere is within
// renderconfig.structures.view_m of the player (layer 0); beyond it, a chunk
// the scope wedge hits (focus.structures range_m) is admitted nearest-to-apex
// under the tier's tri / draw budget onto focus.layer — the optic cameras
// alone draw it (APP_SURFACE §Rev 84 focus: the naked-eye pass is unchanged).
// Frustum culling per mesh (bounding sphere).
//
// Far ground (structplan.js FAR_RULE): with the structures GROUND artifact
// (dd.ground, T0 - y per vertex) every vertex of a far:'drop' kind with
// weight w > 0 (farContacts: 1 on the skin / buried, fading to 0 at
// far_fade_m over T0; 0 at a seam with a far:'ribbon' record) is moved by
// the SIGNED w * (T0 - G) in the drawn groups, G = the ground the terrain
// manager really draws there (terrain.groundDrawn: the ground tree's one
// surface, naked eye and optic alike) — the feet sit on the drawn ground
// exactly as they sit on L0, whether it lies below or above the true
// ground. A CPU pass over the contact vertices only (a sub-range upload),
// time-sliced (DRAW.far_slice_ms), started at most every DRAW.far_ms after
// DRAW.far_step_m of movement, a terrain change or a newly shown group. No
// artifact -> no pass: the static draw.
// FAR BODY RULE (structplan farBodies): a fill / cut that touches no static
// record moves AS A WHOLE past view_m — every vertex, crest included, ramps
// to weight 1 over DRAW.far_body_ramp_m (farBodyWeight, by the vertex's
// distance from the pass viewer), so it keeps its true height over the far
// ground instead of stretching from its crest down to a window-min band 45-65 m
// low. Inside view_m the far_fade weights alone: a vertex whose weight is 0
// there costs no ground sample.
//
// Perf (APP_SURFACE §Perf pass): opaque + depth-writing, default renderOrder
// (the sky dome after it, the panorama before it). Both programs are
// compiled by warmShaders: empty-geometry keeper meshes carrying the SAME
// materials join the scene at construction, before the briefing warm-up.
import * as THREE from 'three';
import { DRAW, residency, thinShown, tiesNear, farBodyWeight, farShift } from './structplan.js';
import { stonePars, stoneMap, STONE_NORMAL } from './stonewall.js';
// (R8c2) the draw's arrays are built by client/src/pool-structures.js (pure,
// the worker's: structDrawArrays); this module wraps them in THREE objects
import { structDrawArrays, unpackTieCells, srgbToLinear, TIE_V } from './pool-structures.js';
import { poolTexture } from './roads3d.js';
import { cpuShort } from './adaptres.js';
export { STONE_CODE, GRATE_BORE_STAIR, GRATE_BORE_TRACK } from './pool-structures.js';

const MOVE_M = 16;                // residency re-test after this much movement
// the CPU-short drape cadence (quest-move 2026-11-02 on the quest: a fast
// flight kept the far pass running back to back, 1.5 ms of every frame; since
// all-res 2026-11-02 also any device whose draw rung measured its CPU short —
// adaptres cpuShort): half the slice, twice the start interval — the same
// pass, landed later; every other device DRAW's own terms
export const FAR_SLICE_QUEST = 0.5, FAR_MS_QUEST = 2;
const _clock = typeof performance !== 'undefined' ? () => performance.now() : () => Date.now();

// the lit batch's detail grain: a neutral speckle (mean DRAW.detail_mean),
// deterministic (same hash as roads3d), sRGB like the roads3d canvases —
// painted in the client worker pool (R8c5: texpaint.js paintDetail through
// roads3d.js poolTexture; run = pool.js run) -> {tex, ready} (tex: no pixels
// until ready); null headless (node gate: untextured). ONE texture for the
// lit and grate batches (main.js passes tex as opts.detail; today's two
// canvases held the same pixels with the same settings).
export function detailTexture(run) {
  if (typeof document === 'undefined') return null;      // node gate: untextured
  return poolTexture(run, 'detail', { mean: DRAW.detail_mean.v });
}

// STAIR nosings in the lit program: a dark band at every riser of world
// height, anti-aliased by the screen-space derivative and faded out where a
// riser is under ~2 px (no moire at distance). No discard. With the speckle
// map (never headless) the FILL STONE too: stair code STONE_CODE draws
// stonewall.js stoneMap in place of the speckle and tilts the normal (relief).
function nosings(mat, steps = null) {
  const riser = (steps ? steps.riser_m : DRAW.riser_m.v).toFixed(4);
  // (A5) the nosing band: nosing_m of each riser, its shade the look's
  // nosing / tread luminance (today: 0.10 of a riser, 38 % darker)
  const lum = (c) => 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  const bandW = (steps ? Math.min(0.5, steps.nosing_m / steps.riser_m) : 0.10).toFixed(4);
  const dark = (steps ? Math.max(-1, Math.min(0.9, 1 - lum(steps.colour.nosing) / lum(steps.colour.tread))) : 0.38).toFixed(4);
  // the stone: one speckle sample either way (its uv picked by the code: uniform control flow, sound
  // derivatives); without a map (node) no texture at all
  const stone = !!mat.map;
  const mapStone = stone ? `#ifdef USE_MAP
      ${stoneMap('step(0.7, vStair) * step(vStair, 0.85)', srgbToLinear(DRAW.detail_mean.v), DRAW.detail_m.v)}
      #endif` : '#include <map_fragment>';
  const sv = stone ? '\nvarying vec3 vWp;\nvarying vec3 vWn;' : '';
  mat.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\nattribute float stair;\nvarying float vStair;\nvarying float vWy;${sv}`)
      .replace('#include <fog_vertex>', `#include <fog_vertex>\nvStair = stair;\nvWy = (modelMatrix * vec4(transformed, 1.0)).y;${stone ? '\nvWp = (modelMatrix * vec4(transformed, 1.0)).xyz;\nvWn = normalize(mat3(modelMatrix) * objectNormal);' : ''}`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\nvarying float vStair;\nvarying float vWy;${sv}${stone ? stonePars() : ''}`)
      .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>${stone ? `\n#ifdef USE_MAP\n${STONE_NORMAL}\n#endif` : ''}`)
      .replace('#include <map_fragment>', `${mapStone}
      if (vStair > 0.9) {
        float q = vWy / ${riser};
        float w = fwidth(q);
        float band = 1.0 - smoothstep(${bandW}, ${bandW} + w, fract(q));
        diffuseColor.rgb *= 1.0 - ${dark} * band * (1.0 - smoothstep(0.25, 0.5, w));
      }`)
      // (P3d) LIGHT_CODE: an emitter (a site's light strip) — its colour unlit, x the luminaire glow over the grain mean
      .replace('#include <opaque_fragment>', `if (vStair > 0.3 && vStair < 0.45) outgoingLight = diffuseColor.rgb * ${(DRAW.fixture_glow.v / srgbToLinear(DRAW.detail_mean.v)).toFixed(4)};\n#include <opaque_fragment>`);
  };
  mat.customProgramCacheKey = () => 'structs2-lit' + (steps ? '-a5' : '') + (stone ? '-stone' : '');
  return mat;
}
// (WAYS-G A3) the GRATE program: the lit program with a discard pattern.
// stair attribute 255 = a funicular-stair sheet (treads by world height at
// DRAW.walkway_riser_m: the first tread_gap of each period open, a nosing
// band after it), else a track strip (sleepers every sleeper_pitch_m of
// plan arc along the strip's fall line: the horizontal part of its normal;
// under a 3 % fall the world x). Where a period spans under ~2 px the
// pattern fades to solid (no shimmer in the distance).
// (D1-TUNC R2) GRATING IN A BORE TAKES THE BORE LIGHT: a grate triangle of a
// srcKind-bores record (the funicular's strip / stair sheets through its
// bore, D1-TUNW) carries stair GRATE_BORE_STAIR (170) / GRATE_BORE_TRACK (85)
// instead of 255 / 0, and its vertex colour is already albedo x the bore
// light (structplan boreLight, like the lining); the program then draws it
// UNLIT and fog-free — the bore look — with the same discard pattern
// (stair > 0.5 still picks the stair pattern). Sunlit grating (255 / 0)
// draws exactly as before: the same program, no new attribute.
function grate(mat) {
  const R = DRAW.walkway_riser_m.v.toFixed(4), gap = DRAW.tread_gap.v.toFixed(4);
  const pitch = DRAW.sleeper_pitch_m.v.toFixed(4), sw = (DRAW.sleeper_w_m.v / DRAW.sleeper_pitch_m.v).toFixed(4);
  mat.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float stair;\nvarying float vStair;\nvarying vec3 vWp;\nvarying vec3 vWn;')
      .replace('#include <fog_vertex>', '#include <fog_vertex>\nvStair = stair;\nvWp = (modelMatrix * vec4(transformed, 1.0)).xyz;\nvWn = normalize(mat3(modelMatrix) * objectNormal);');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying float vStair;\nvarying vec3 vWp;\nvarying vec3 vWn;')
      .replace('#include <map_fragment>', `#include <map_fragment>
      {
        float q, open, w;
        if (vStair > 0.5) {
          q = vWp.y / ${R};
          w = fwidth(q);
          float f = fract(q);
          open = step(f, ${gap});
          float band = 1.0 - smoothstep(${gap} + 0.08, ${gap} + 0.08 + w, f);
          diffuseColor.rgb *= 1.0 - 0.35 * band * (1.0 - open) * (1.0 - smoothstep(0.25, 0.5, w));
        } else {
          vec2 h = vWn.xz;
          float l = length(h);
          float s = l > 0.03 ? dot(vWp.xz, h / l) : vWp.x;
          q = s / ${pitch};
          w = fwidth(q);
          open = step(${sw}, fract(q));
        }
        if (open > 0.5 && w < 0.35) discard;
      }`)
      .replace('#include <opaque_fragment>', 'if (vStair > 0.2 && vStair < 0.8) outgoingLight = diffuseColor.rgb;\n#include <opaque_fragment>')
      .replace('#include <fog_fragment>', 'if (!(vStair > 0.2 && vStair < 0.8)) {\n#include <fog_fragment>\n}');
  };
  mat.customProgramCacheKey = () => 'structs2-grate';
  return mat;
}
function boreProgram(mat) {
  mat.customProgramCacheKey = () => 'structs2-bore';
  return mat;
}

export class StructDraw {
  // cfg = renderconfig.structures ({view_m, ...}); opts = {bore, detail,
  // steps} (main.js passes the pool-painted textures: roads3d boreTexture(run)
  // .tex, detailTexture(run).tex — drawn only once both landed; no detail:
  // null, untextured as headless — a material without a map draws the same
  // vertex colours).
  // ONE layout, the draw groups above; drawnTri maps each STR1 triangle to
  // its drawn copy (mesh index, first vertex; -1 NO_RENDER) — the drawn ==
  // felt gate (check-emit --bake) reads it.
  constructor(scene, cfg, opts = {}) {
    this.scene = scene;
    this.viewM = (cfg && cfg.view_m) || 1360;
    this.materials = {
      lit: nosings(new THREE.MeshLambertMaterial({ vertexColors: true, map: opts.detail || null, side: THREE.DoubleSide }), opts.steps || null),
      bore: boreProgram(new THREE.MeshBasicMaterial({ vertexColors: true, map: opts.bore || null, fog: false, side: THREE.DoubleSide })),
      grate: grate(new THREE.MeshLambertMaterial({ vertexColors: true, map: opts.detail || null, side: THREE.DoubleSide })),
    };
    // (A5) L's stair look: the raised flights' tread colour (sRGB bytes)
    this.steps = opts.steps || null;
    if (this.steps) checkSteps(this.steps);
    this.keepers = [];
    for (const [k, m] of Object.entries(this.materials)) {
      const g = new THREE.BufferGeometry();
      if (k !== 'bore') g.setAttribute('stair', new THREE.BufferAttribute(new Uint8Array(0), 1, true));
      const keeper = new THREE.Mesh(g, m);
      keeper.frustumCulled = false;
      keeper.name = 'structs2-keeper-' + k;
      scene.add(keeper);
      this.keepers.push(keeper);
    }
    this.groups = [];
    this.chunks = [];             // per group {x, z, r, n, draws} (structplan residency)
    this._st = new Int8Array(0);
    this.meshes = [];             // every chunk mesh (the smoke count)
    this.tris = 0;
    this.drawnTri = null;         // per STR1 triangle: {mesh, vert} of its drawn copy
    this.last = new THREE.Vector3(1e9, 0, 1e9);
    this.fgen = -2; this.fon = false;
    this.stats = null;
    // FAR_RULE pass state: last pass viewer / terrain gen / start time, the
    // chunk list + cursor of the pass in progress, its cost (ms, samples, frames)
    this.drape = { at: new THREE.Vector3(1e9, 0, 1e9), gen: -1, t: -1e9, list: [], cur: -1, k: 0, chg: false, max: 0, ms: 0, n: 0, frames: 0, pending: false, si: 0, wi: 0 };
    // (D1-TUNC) the near-sleeper blocks: their groups, the last re-fill viewer, scratch
    this.tieGroups = []; this.tieAt = new THREE.Vector3(1e9, 0, 1e9); this.tiesShown = 0; this._tp = []; this._td = [];
  }

  // str: the loadStructures object the collider uses; dd: {bores, rails}
  // from ways.json (null: no rails, lamp shading at its mean) + ground — the
  // arrays (pool-structures.js structDrawArrays, the pool's
  // 'structures.draw' op runs the same) wrapped here
  build(str, dd) {
    this.dispose();
    return this.wrap(structDrawArrays(str, dd, this.steps));
  }

  // the draw from structDrawArrays' arrays (main.js: the 'structures.draw'
  // job's result): per draw group a THREE.Group of at most three meshes —
  // lit, bore, grate, in that order — over the arrays as they are (bounding
  // spheres included: three's own, computed with the arrays)
  wrap(A) {
    this.dispose();
    const sphere = (g, bs) => { g.boundingSphere = new THREE.Sphere(new THREE.Vector3(bs[0], bs[1], bs[2]), bs[3]); };
    for (const q of A.groups) {
      const grp = new THREE.Group();
      grp.name = `structs2-${q.cx},${q.cz}`;
      grp.userData = { x: q.x, z: q.z, r: q.r, n: q.n, nHead: q.nHead, draws: q.draws, state: 1, thin: 1, heads: [] };
      let meshL = null, meshB = null, meshG = null;
      // the lit and the grate batch: one attribute layout
      const litMesh = (Q, mat, tag) => {
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.BufferAttribute(Q.pos, 3));
        g.setAttribute('normal', new THREE.BufferAttribute(Q.nor, 3));
        g.setAttribute('color', new THREE.BufferAttribute(Q.col, 3, true));
        g.setAttribute('uv', new THREE.BufferAttribute(Q.uv, 2));
        g.setAttribute('stair', new THREE.BufferAttribute(Q.st, 1, true));
        sphere(g, Q.bs);
        const m = new THREE.Mesh(g, mat);
        m.name = grp.name + '-' + tag;
        if (Q.head < Q.n * 3) grp.userData.heads.push([g, Q.head]);
        m.matrixAutoUpdate = false;
        return m;
      };
      if (q.L) {
        meshL = litMesh(q.L, this.materials.lit, 'lit');
        grp.add(meshL); this.meshes.push(meshL);
      }
      if (q.B) {
        const Q = q.B, g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.BufferAttribute(Q.pos, 3));
        g.setAttribute('color', new THREE.BufferAttribute(Q.col, 3));
        g.setAttribute('uv', new THREE.BufferAttribute(Q.uv, 2));
        sphere(g, Q.bs);
        const m = new THREE.Mesh(g, this.materials.bore);
        m.name = grp.name + '-bore';
        meshB = m;
        if (q.ts) {
          // the sphere holds every sleeper the block can show (Q.bs); the
          // block is drawn only as far as update() filled it
          const T = q.ts;
          const ts = { p: { tieXZ: T.tieXZ, tieBB: Array.from(T.tieBB), tieCells: unpackTieCells(T.tieCells) }, base: T.base, cap: T.cap, n: 0, pos: T.pos, col: T.col, over: 0 };
          g.setDrawRange(0, ts.base);
          ts.g = g;
          grp.userData.ties = ts;
          grp.userData.heads.push([g, Q.head, ts]);
        } else if (Q.head < Q.n * 3) grp.userData.heads.push([g, Q.head]);
        m.matrixAutoUpdate = false;
        grp.add(m); this.meshes.push(m);
      }
      if (q.G) {
        meshG = litMesh(q.G, this.materials.grate, 'grate');
        grp.add(meshG); this.meshes.push(meshG);
      }
      if (q.foot) {
        const f = q.foot, M = [meshL, meshB, meshG];
        grp.userData.foot = { x: f.x, y: f.y, z: f.z, t0: f.t0, w: f.w, b: f.b, drop: new Float32Array(f.x.length),
          sides: f.sides.map((s) => ({ m: M[s.k], r0: M[s.k].geometry.boundingSphere.radius, pairs: s.pairs, lo: s.lo, hi: s.hi })) };
      }
      grp.matrixAutoUpdate = false;
      this.scene.add(grp);
      this.groups.push(grp);
    }
    const st = A.stats;
    this.tris = st.lit + st.bore + st.grate;
    this.drawnTri = { mesh: A.triMesh, vert: A.triVert };
    this.chunks = this.groups.map((g) => g.userData);
    this._st = new Int8Array(this.groups.length);
    this.stats = st;
    this.tieGroups = this.groups.filter((g) => g.userData.ties);
    this.tieAt.set(1e9, 0, 1e9);
    this.last.set(1e9, 0, 1e9);
    Object.assign(this.drape, { gen: -1, t: -1e9, list: [], cur: -1, k: 0, si: 0, wi: 0, pending: false });
    this.drape.at.set(1e9, 0, 1e9);
    this.fgen = -2;
    return this;
  }

  // residency: naked-eye view range, then the scope wedge (focus.structures);
  // then the FAR_RULE drape of the drawn chunks over `terrain` (optional)
  update(pos, focus, terrain) {
    if (!this.groups.length) return;
    const t0 = _clock();
    const fc = (focus && focus.active && focus.structures) || null;
    const gen = fc ? focus.gen : -1;
    const moved = Math.hypot(pos.x - this.last.x, pos.z - this.last.z) > MOVE_M;
    let shown = false;
    if (moved || gen !== this.fgen || !!fc !== this.fon) {
      if (moved) this.last.copy(pos);
      this.fgen = gen; this.fon = !!fc;
      const st = residency(this.chunks, pos.x, pos.z, this.viewM, focus, this._st);
      for (let i = 0; i < this.groups.length; i++) {
        const u = this.groups[i].userData, was = u.state;
        this._state(this.groups[i], st[i], st[i] === 2 ? focus.layer : 0);
        if (st[i] > 0 && was <= 0) shown = true;
        // THE THIN-KIND VIEW RULE: the naked eye draws a batch's thin tail
        // within thin_view_m only; the scope (state 2) the whole group
        const full = st[i] === 2 || thinShown(u, pos.x, pos.z, this.viewM) ? 1 : 0;
        if (full !== u.thin) {
          u.thin = full;
          this._range(u);
        }
      }
    }
    this._ties(pos);
    if (!terrain || !this.stats || !this.stats.contacts) return;
    // a newly shown group is LATCHED until a pass starts (a pass in progress
    // or the far_ms throttle must not swallow it); the slice budget counts
    // from t0, so this frame's residency pass is paid out of it
    if (shown) this.drape.pending = true;
    this._drape(pos, terrain, t0);
  }

  // a group's draw ranges: beyond thin_view_m its heads; else everything —
  // a bore batch with a sleeper block up to the sleepers update() placed
  _range(u) {
    for (const [g, head, ts] of u.heads) g.setDrawRange(0, u.thin ? (ts ? ts.base + ts.n * TIE_V : Infinity) : head);
  }

  // (D1-TUNC) THE NEAR SLEEPERS IN A BORE: after DRAW.tie_step_m of
  // movement, every sleeper block is re-filled with its group's sleepers
  // within DRAW.tie_view_m (structplan tiesNear; nearest first should one
  // ever exceed the block, tieCap: an upper bound, so never), one sub-range
  // upload of position + colour; no far sleepers in bores
  _ties(pos) {
    if (!this.tieGroups.length || Math.hypot(pos.x - this.tieAt.x, pos.z - this.tieAt.z) < DRAW.tie_step_m.v) return;
    this.tieAt.copy(pos);
    const R = DRAW.tie_view_m.v, pick = this._tp, d2 = this._td;
    let shown = 0;
    for (const grp of this.tieGroups) {
      const u = grp.userData, ts = u.ties;
      pick.length = 0; d2.length = 0;
      tiesNear(ts.p, pos.x, pos.z, R, pick, d2);
      let n = pick.length;
      if (n === 0 && ts.n === 0) continue;
      let order = null;
      if (n > ts.cap) {
        order = pick.map((_, k) => k).sort((a, b) => d2[a] - d2[b]);
        ts.over += n - ts.cap; n = ts.cap;
      }
      const ga = ts.g.attributes, P = ga.position.array, C = ga.color.array, w = TIE_V * 3;
      for (let k = 0; k < n; k++) {
        const j = order ? pick[order[k]] : pick[k], dst = (ts.base + k * TIE_V) * 3;
        P.set(ts.pos.subarray(j * w, j * w + w), dst);
        C.set(ts.col.subarray(j * w, j * w + w), dst);
      }
      if (n) {
        for (const a of [ga.position, ga.color]) { a.addUpdateRange(ts.base * 3, n * w); a.needsUpdate = true; }
      }
      ts.n = n;
      shown += n;
      this._range(u);
    }
    this.tiesShown = shown;
  }

  // FAR_RULE pass (structplan.js): move each contact vertex of a drawn group
  // by the signed w * (T0 - G), G = terrain.groundDrawn (the ground tree's
  // drawn surface, one for naked eye and optic) -> position. A pass is
  // TIME-SLICED: about DRAW.far_slice_ms per frame, counted from the start of
  // update (the residency pass is paid out of it): a cursor over the drawn
  // groups and their contacts, 256 samples between clock reads; a group's
  // write follows its last sample block in steps of DRAW.far_write_n
  // vertices, and its upload (one sub-range per mesh and attribute) is
  // published after the last one. A full pass (13-16 ms in node at the
  // densest spot, $A/tools/fartest.mjs) never lands in one frame; a frame
  // overshoots the budget by at most one step (256 samples or far_write_n
  // written vertices, ~0.3 ms).
  _drape(pos, terrain, now) {
    const d = this.drape, q = cpuShort();
    const budget = DRAW.far_slice_ms.v * (q ? FAR_SLICE_QUEST : 1), farMs = DRAW.far_ms.v * (q ? FAR_MS_QUEST : 1);
    if (d.cur < 0 || d.cur >= d.list.length) {
      const stepped = Math.hypot(pos.x - d.at.x, pos.z - d.at.z) >= DRAW.far_step_m.v;
      if (!stepped && !d.pending && terrain.gen === d.gen) return;
      if (now - d.t < farMs) return;
      // a new pass: the drawn chunks with contacts, from this viewer
      d.t = now; d.gen = terrain.gen; d.at.copy(pos); d.pending = false;
      d.list = this.groups.filter((g) => g.userData.foot && g.userData.state > 0);
      d.cur = 0; d.k = 0; d.si = 0; d.wi = 0; d.n = 0; d.ms = 0; d.frames = 0;
    }
    const vx = d.at.x, vz = d.at.z, viewM = this.viewM;
    d.frames++;
    while (d.cur < d.list.length) {
      const g = d.list[d.cur];
      const f = g.userData.foot;
      // resumable inside a group: d.k = next contact, d.chg / d.max carried
      if (d.k === 0 && d.si === 0 && d.wi === 0) { d.chg = false; d.max = 0; }
      const n = f.x.length;
      while (d.k < n) {
        const end = Math.min(n, d.k + 256);
        for (let k = d.k; k < end; k++) {
          const w = f.b[k] ? farBodyWeight(f.w[k], 1, Math.hypot(f.x[k] - vx, f.z[k] - vz), viewM) : f.w[k];
          // w 0: a body vertex inside view_m — static, no sample
          const dr = w === 0 ? 0 : farShift(w, f.t0[k], terrain.groundDrawn(f.x[k], f.z[k]));
          if (Math.abs(dr - f.drop[k]) > 0.005) { f.drop[k] = dr; d.chg = true; }
          const m = Math.abs(f.drop[k]);
          if (m > d.max) d.max = m;
        }
        d.n += end - d.k;
        d.k = end;
        if (_clock() - now >= budget) { d.ms += _clock() - now; return; }   // d.k === n: the upload is next frame's first step
      }
      // the write: DRAW.far_write_n drawn vertices per step (the densest
      // group writes 33,572 — 2.6 ms in one piece), clock read between
      // steps; the CPU arrays change unseen until the group's LAST step
      // publishes every side at once (needsUpdate), so no frame draws a
      // half-written group
      if (d.chg) {
        const S = f.sides, step = DRAW.far_write_n.v * 2;
        while (d.si < S.length) {
          const sd = S[d.si], P = sd.pairs, ga = sd.m.geometry.attributes;
          const arr = ga.position.array, end = Math.min(P.length, d.wi + step);
          for (let i = d.wi; i < end; i += 2) {
            const o = P[i], uid = P[i + 1];
            arr[o * 3 + 1] = f.y[uid] - f.drop[uid];
          }
          d.wi = end;
          if (d.wi >= P.length) { d.si++; d.wi = 0; }
          if (d.si < S.length && _clock() - now >= budget) { d.ms += _clock() - now; return; }
        }
        for (const sd of S) {
          const ga = sd.m.geometry.attributes;
          // no clearUpdateRanges: three uploads and clears the ranges itself,
          // and a sleeper re-fill (_ties) may have queued one this frame
          ga.position.addUpdateRange(sd.lo * 3, (sd.hi - sd.lo + 1) * 3);
          ga.position.needsUpdate = true;
          // the sphere holds every vertex at its draped height
          sd.m.geometry.boundingSphere.radius = sd.r0 + d.max;
        }
      }
      d.cur++; d.k = 0; d.si = 0; d.wi = 0;
      if (_clock() - now >= budget) { d.ms += _clock() - now; return; }
    }
    d.ms += _clock() - now;
  }

  _state(g, state, layer) {
    const u = g.userData;
    g.visible = state > 0;
    if (state > 0 && u.layer !== layer) {
      for (const m of g.children) m.layers.set(layer);
      u.layer = layer;
    }
    u.state = state;
  }

  dispose() {
    for (const g of this.groups) {
      this.scene.remove(g);
      for (const m of g.children) m.geometry.dispose();
    }
    this.groups = [];
    this.chunks = [];
    this.meshes = [];
    this.tieGroups = [];
    this.tris = 0;
  }
}

// (A5) L's stair look ("steps" of pipeline/wayslook.json): the fields the
// raised flights read, checked once (fail fast on a schema change)
export function checkSteps(st) {
  const okC = (c) => Array.isArray(c) && c.length === 3 && c.every((v) => v >= 0 && v <= 255);
  if (!st || !(st.riser_m > 0.05 && st.riser_m < 0.4) || !(st.nosing_m >= 0 && st.nosing_m < st.riser_m) || !st.colour || !okC(st.colour.tread) || !okC(st.colour.nosing)) {
    throw new Error(`structs2: the stair look (pipeline/wayslook.json steps: riser_m, nosing_m, colour.tread / nosing) is not the schema A5 reads: ${JSON.stringify(st)}`);
  }
  return st;
}
